import React, { useState, useEffect } from "react";
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  IconButton, Typography, Box, Button, TextField, List, ListItem, ListItemText,
  FormControlLabel, Checkbox, Paper, Divider, Chip, Tooltip, Stack
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DownloadIcon from "@mui/icons-material/Download";
import SaveIcon from "@mui/icons-material/Save";
import PatchReviewDialog from "./PatchReviewDialog";
import DiffReviewDialog from "./DiffReviewDialog";

async function postJson(url, body) {
  const res = await fetch(url, {
    method: "POST",
    headers: {"Content-Type":"application/json"},
    body: JSON.stringify(body || {})
  });
  return await res.json();
}

function toMarkdown(data) {
  const steps = (data.plan?.steps || []).map((s, i) => `${i+1}. ${s}`).join("\n");
  const notes = (data.plan?.file_notes || [])
    .map(n => `- \`${n.path}\`: ${n.change_summary}`).join("\n");
  const reads = (data.reads || [])
    .map(r => {
      const status = r.ok
        ? (r.truncated ? "OK (truncated)" : "OK")
        : ("ERROR: " + (r.error || "read failed"));
      return `- \`${r.path}\` • ${status}`;
    })
    .join("\n");
  return `# Planner Instruction

**Module:** ${data.module}
**Generated:** ${data.generated_at}

## Summary
${data.plan?.summary || ""}

## Steps
${steps}

## File Notes
${notes}

## Read Status
${reads}

## Risks / Checks
${(data.plan?.risks || []).map(r => `- ${r}`).join("\n")}
`;
}

export default function PlannerInstructorDialog({ open, onClose, moduleName, defaultFiles, defaultMessage }) {
  const [message, setMessage] = useState(defaultMessage || "");
  const [useGpt, setUseGpt] = useState(true);
  const [files, setFiles] = useState(defaultFiles || []);
  const [result, setResult] = useState(null);
  const [err, setErr] = useState("");

  // Patch Review modal state (queue + run-once artifacts)
  const [patchReviewOpen, setPatchReviewOpen] = useState(false);
  const [patchReviewData, setPatchReviewData] = useState(null);

  // Diff Review modal state (GPT diffs + apply)
  const [diffReviewOpen, setDiffReviewOpen] = useState(false);
  const [diffReviewData, setDiffReviewData] = useState(null);

  useEffect(() => { setFiles(defaultFiles || []); }, [defaultFiles]);
  useEffect(() => { setMessage(defaultMessage || ""); }, [defaultMessage]);

  const onRun = async () => {
    setErr(""); setResult(null);
    const r = await postJson("/api/planner/plan", {
      module: moduleName || "template_generator",
      message,
      files,
      use_gpt: useGpt
    });
    if (!r.ok) { setErr((r.errors && r.errors[0]) || "Planner failed"); return; }
    setResult(r.data);
  };

  const copyText = async (text) => {
    try { await navigator.clipboard.writeText(text); } catch {}
  };

  const copySummary = () => result && copyText(result.plan?.summary || "");
  const copySteps = () => result && copyText((result.plan?.steps || []).map((s,i)=>`${i+1}. ${s}`).join("\n"));
  const copyAll = () => result && copyText(toMarkdown(result));

  const downloadMarkdown = () => {
    if (!result) return;
    const blob = new Blob([toMarkdown(result)], { type: "text/markdown" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `planner_${(moduleName||"module")}_${Date.now()}.md`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const saveSnapshot = async () => {
    if (!result) return;
    const r = await postJson("/api/planner/plan/snapshot", {
      module: moduleName || "template_generator",
      result_json: result
    });
    if (!r.ok) { setErr((r.errors && r.errors[0]) || "Snapshot failed"); return; }
  };

  // Queue to Patch Assistant and open Patch Review modal
  const sendToPatcher = async () => {
    try {
      const payload = {
        module: moduleName || "unknown_module",
        message,
        files,
        plan: result?.plan || { steps: [], actions: [], risks: [], file_notes: [] }
      };

      const queued = await postJson("/api/patch/queue", payload);
      if (!queued?.ok) {
        alert((queued?.errors && queued.errors[0]) || "Failed to queue patch");
        return;
      }

      const run = await postJson("/api/patch/run-once", {});
      if (!run?.ok) {
        alert((run?.errors && run.errors[0]) || "Patch assistant failed");
        return;
      }

      setPatchReviewData(run.data || {});
      setPatchReviewOpen(true);
    } catch (e) {
      console.error(e);
      alert("Unexpected error sending to patcher");
    }
  };

  // Ask backend to get GPT-proposed diffs
  const proposeDiffs = async () => {
    if (!result) { setErr("Run 'Propose Instruction' first."); return; }
    try {
      const payload = {
        module: moduleName || "unknown_module",
        message,
        files,
        plan: result?.plan || { steps: [], actions: [], risks: [], file_notes: [] }
      };
      const res = await postJson("/api/planner/propose_diffs", payload);
      if (!res?.ok) { setErr((res?.errors && res.errors[0]) || "Failed to get diffs"); return; }
      setDiffReviewData(res.data || {});
      setDiffReviewOpen(true);
    } catch (e) {
      console.error(e);
      setErr("Unexpected error proposing diffs");
    }
  };

  // Apply GPT-proposed diffs (send path + resolved_path + new_content)
  const applyDiffs = async () => {
    try {
      const files = (diffReviewData?.files || []).map(f => ({
        path: f.path,
        resolved_path: f.resolved_path,
        new_content: f.new_content
      }));
      const res = await postJson("/api/patch/apply", { files });
      if (!res?.ok) { alert((res?.errors && res.errors[0]) || "Apply failed"); return; }
      alert(`Applied ${res.data?.count || 0} file(s).\n${(res.data?.applied || []).join("\n")}`);
      setDiffReviewOpen(false);
    } catch (e) {
      console.error(e);
      alert("Unexpected error applying patch");
    }
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg" PaperProps={{ sx:{ height:"90vh" } }}>
        <DialogTitle sx={{ pr: 6 }}>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Typography variant="h6">Planner / Instructor</Typography>
            <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers sx={{ p:2 }}>
          <Paper variant="outlined" sx={{ p:2, mb:2 }}>
            <Typography variant="subtitle2" sx={{ mb:1 }}>Inputs</Typography>
            <Box display="flex" gap={2} alignItems="center" flexWrap="wrap">
              <Typography variant="body2"><b>Module:</b> {moduleName || "template_generator"}</Typography>
              <FormControlLabel control={<Checkbox checked={useGpt} onChange={e=>setUseGpt(e.target.checked)} />} label="Use GPT" />
            </Box>
            <TextField
              sx={{ mt:1 }}
              label="Message / intent"
              value={message}
              onChange={e=>setMessage(e.target.value)}
              fullWidth size="small"
            />
            <Typography variant="subtitle2" sx={{ mt:2 }}>Files to read</Typography>
            <TextField
              placeholder="One path per line (relative to sandbox roots)"
              value={(files||[]).join("\n")}
              onChange={e=>setFiles(e.target.value.split("\n").map(s=>s.trim()).filter(Boolean))}
              multiline minRows={3} maxRows={8} fullWidth size="small"
            />
            <Button sx={{ mt:2 }} variant="contained" onClick={onRun}>Propose Instruction</Button>
            {err && <Typography color="error" sx={{ mt:1 }}>{err}</Typography>}
          </Paper>

          {result && (
            <Box>
              {/* Read status */}
              <Paper variant="outlined" sx={{ p:2, mb:2 }}>
                <Typography variant="subtitle2">Read Status</Typography>
                <List dense>
                  {(result.reads || []).map((r,i)=>(
                    <ListItem key={i}>
                      <ListItemText
                        primary={r.path}
                        secondary={r.ok ? (r.truncated ? "OK (truncated)" : "OK") : `ERROR: ${r.error || "read failed"}`}
                      />
                      <Chip size="small" color={r.ok ? "success" : "error"} label={r.ok ? (r.truncated ? "TRUNCATED" : "OK") : "ERROR"} />
                    </ListItem>
                  ))}
                </List>
              </Paper>

              {/* Summary */}
              <Paper variant="outlined" sx={{ p:2, mb:2 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography variant="subtitle2">Summary</Typography>
                  <Tooltip title="Copy summary">
                    <IconButton size="small" onClick={copySummary}><ContentCopyIcon fontSize="small" /></IconButton>
                  </Tooltip>
                </Stack>
                <Typography variant="body2" sx={{ whiteSpace:"pre-wrap" }}>{result.plan?.summary || ""}</Typography>
              </Paper>

              {/* Steps */}
              <Paper variant="outlined" sx={{ p:2, mb:2 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography variant="subtitle2">Steps</Typography>
                  <Tooltip title="Copy steps">
                    <IconButton size="small" onClick={copySteps}><ContentCopyIcon fontSize="small" /></IconButton>
                  </Tooltip>
                </Stack>
                <List dense>
                  {(result.plan?.steps || []).map((s,i)=>(
                    <ListItem key={i}>
                      <ListItemText primary={`${i+1}. ${s}`} />
                    </ListItem>
                  ))}
                </List>
              </Paper>

              {/* File Notes */}
              <Paper variant="outlined" sx={{ p:2, mb:2 }}>
                <Typography variant="subtitle2">File Notes</Typography>
                <Divider sx={{ my:1 }} />
                <List dense>
                  {(result.plan?.file_notes || []).map((f,i)=>(
                    <ListItem key={i}>
                      <ListItemText
                        primary={<span style={{fontFamily:"monospace"}}>{f.path}</span>}
                        secondary={f.change_summary}
                      />
                    </ListItem>
                  ))}
                </List>
              </Paper>

              {/* Risks + actions */}
              <Paper variant="outlined" sx={{ p:2, mb:2 }}>
                <Typography variant="subtitle2">Risks / Checks</Typography>
                <List dense>
                  {(result.plan?.risks || []).map((r,i)=>(
                    <ListItem key={i}><ListItemText primary={`• ${r}`} /></ListItem>
                  ))}
                </List>
                <Stack direction="row" spacing={1}>
                  <Button startIcon={<SaveIcon />} variant="outlined" onClick={saveSnapshot}>Save Snapshot</Button>
                  <Button startIcon={<ContentCopyIcon />} variant="outlined" onClick={copyAll}>Copy Plan (Markdown)</Button>
                  <Button startIcon={<DownloadIcon />} variant="outlined" onClick={downloadMarkdown}>Download .md</Button>
                  <Button variant="outlined" onClick={proposeDiffs}>Propose Diffs (GPT)</Button>
                  <Button variant="contained" color="primary" onClick={sendToPatcher}>
                    Send to Patcher & Review
                  </Button>
                </Stack>
              </Paper>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p:2 }}>
          <Button onClick={onClose}>Close</Button>
        </DialogActions>
      </Dialog>

      <PatchReviewDialog
        open={patchReviewOpen}
        onClose={() => setPatchReviewOpen(false)}
        data={patchReviewData}
      />
      <DiffReviewDialog
        open={diffReviewOpen}
        onClose={() => setDiffReviewOpen(false)}
        data={diffReviewData}
        onApply={applyDiffs}
      />
    </>
  );
}
